import { useMutation } from '@tanstack/react-query'
import { syncraApi } from '@/api/main/syncraClient'
import { useActiveUser } from '@/shared/hooks/useActiveUser'
import { Roles } from '@/entities/roles'
import type { ExportType } from '../model'

type ExportParams = {
  startDate?: string
  endDate?: string
  statuses?: number[]
}

type ExportVariables = {
  type: ExportType
  params?: ExportParams
}

type ExportResponse = {
  data: Blob | string
  status: number
}

/**
 * Хук для выполнения экспорта данных
 * Использует syncraApi и useMutation для управления состоянием
 */
export const useExport = () => {
  const { userRole } = useActiveUser()

  const mutation = useMutation<ExportResponse, Error, ExportVariables>({
    mutationFn: async ({ type, params }) => {
      if (!userRole) {
        throw new Error('User role is not defined')
      }
      
      const exportMethod = getExportMethod(userRole, type)
      
      if (!exportMethod) {
        throw new Error(`Неподдерживаемая комбинация роли (${userRole}) и типа экспорта (${type})`)
      }

      return exportMethod(params || {})
    }
  })

  return {
    exportData: mutation.mutateAsync,
    isLoading: mutation.isPending,
    error: mutation.error,
    isSuccess: mutation.isSuccess
  }
}

/**
 * Возвращает соответствующий метод API для экспорта
 */
const getExportMethod = (userRole: Roles, exportType: ExportType) => {
  const apiPrefix = userRole === Roles.Merchant ? 'merchant' : 'trader'
  const methodMap: Record<ExportType, keyof typeof syncraApi.v1> = {
    balances: `${apiPrefix}ExportBalancesCsvList` as keyof typeof syncraApi.v1,
    withdrawals: `${apiPrefix}ExportWithdrawalsCsvList` as keyof typeof syncraApi.v1,
    topups: `${apiPrefix}ExportTopupsCsvList` as keyof typeof syncraApi.v1,
    cryptowithdrawals: `${apiPrefix}ExportCryptowithdrawalsCsvList` as keyof typeof syncraApi.v1,
  }

  const methodName = methodMap[exportType]
  const method = syncraApi.v1[methodName] as any

  if (!method || typeof method !== 'function') {
    return null
  }

  return method.bind(syncraApi.v1)
}

